import { useEffect, useMemo, useState } from "react"

import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaNewsSectionsKey } from "@/lib/extract-batch-requests"
import { CategoryData, PostData } from "@/types/types"
import { CategoryConfig } from "../types/config"
import { SectionData } from "../types/new"
import { mapPostToArticle } from "../utils/article"
import { createCategoriesSignature, normalizeCategories } from "../utils/category"

type UseNewsSectionsResult = {
  sections: SectionData[]
  loading: boolean
}

const buildFallbackSection = (categoryId: string, categoryName: string, slug: string): SectionData => ({
  categoryId,
  title: categoryName,
  slug,
  main: null,
  highlights: [],
  bullets: [],
})

const buildSectionFromPosts = (category: { id: string; name: string; slug: string }, posts: PostData[]): SectionData => {
  if (posts.length === 0) {
    return buildFallbackSection(category.id, category.name, category.slug || "")
  }

  const articles = posts.map(mapPostToArticle)
  const firstPost = posts[0]
  let categorySlug = category.slug || ""

  if (!categorySlug && firstPost?.attributes?.categories?.data) {
    const matchingCategory = firstPost.attributes.categories.data.find(
      (postCategory: CategoryData) => String(postCategory.id) === category.id,
    )

    if (matchingCategory?.attributes?.slug) {
      categorySlug = matchingCategory.attributes.slug
    }
  }

  const postsDataMap = new Map<string, PostData>()
  posts.forEach(post => {
    postsDataMap.set(post.id.toString(), post)
  })

  return {
    categoryId: category.id,
    title: category.name,
    slug: categorySlug,
    main: articles[0] || null,
    highlights: articles.slice(1, 4),
    bullets: articles.slice(4, 7),
    postsData: postsDataMap,
  }
}

export const useNewsSections = (
  categoriesConfig: CategoryConfig[] = [],
  componentId?: string,
): UseNewsSectionsResult => {
  const normalizedCategories = useMemo(() => normalizeCategories(categoriesConfig), [categoriesConfig])
  const categoriesSignature = useMemo(() => createCategoriesSignature(normalizedCategories), [normalizedCategories])
  const batchData = useBatchDataMap()
  const [sections, setSections] = useState<SectionData[]>([])
  const [loading, setLoading] = useState(false)

  const prefetchedSections = useMemo(
    () =>
      normalizedCategories.map(category => {
        if (!componentId) {
          return undefined
        }

        const response = resolveBatchDataEntry(batchData, {
          key: buildTamdaMediaNewsSectionsKey(componentId, category.id),
          prefix: "tamda-media-news-layout:",
          suffix: `:sections:${category.id}`,
        })
        const posts = Array.isArray(response?.data) ? (response.data as PostData[]) : undefined

        if (!posts) {
          return undefined
        }

        return buildSectionFromPosts(category, posts)
      }),
    [batchData, componentId, normalizedCategories],
  )

  useEffect(() => {
    if (normalizedCategories.length === 0) {
      setSections([])
      setLoading(false)
      return
    }

    const hasMissingPrefetch = prefetchedSections.some(section => !section)

    setSections(
      prefetchedSections.map((section, index) => {
        if (section) {
          return section
        }

        const category = normalizedCategories[index]
        return buildFallbackSection(category.id, category.name, category.slug || "")
      }),
    )
    setLoading(hasMissingPrefetch)
  }, [categoriesSignature, normalizedCategories, prefetchedSections])

  return {
    sections,
    loading,
  }
}
